import { useContext, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import useInventoryApi from 'hooks/useInventoryApi';
import { InvoiceFormValues } from '../Constants/invoiceFormikData';
import invoiceApi from '../helpers/invoiceApi';
import listPriceResource from '../helpers/listPriceApi';
import buildListPriceUpdates from '../helpers/buildListPriceUpdates';
import { getSelectedCropSeasonIds } from '../helpers/breadcrumbCropSeasons';
import registerInvoiceProductsOnList, {
  ProductListApis
} from '../helpers/registerInvoiceProductsOnList';

const handleValidationError = (err: any): string => {
  if (err.response?.data?.validationErrors) {
    return err.response.data.validationErrors
      .map((e: any) => e.errorMessage)
      .join(', ');
  }
  if (err.response?.data?.displayErrorMessage) {
    return err.response.data.displayErrorMessage;
  }
  return '';
};

const mapFormToApi = (values: InvoiceFormValues, id?: string) => {
  const invoiceDateEpoch = values.invoiceDate
    ? Date.parse(values.invoiceDate.toISOString())
    : null;
  const invoiceDueDateEpoch = values.invoiceDueDate
    ? Date.parse(values.invoiceDueDate.toISOString())
    : null;
  // Crop seasons are saved according to the current Breadcrumb selection (both
  // create and edit flows), not a per-invoice picker — see FR-069. Reading the
  // breadcrumb at save time (rather than a Formik field seeded on mount) means
  // the saved seasons always match what's selected, regardless of which step
  // the user saves from.
  //
  // Drop null/empty season ids: the invoice service maps each element to its
  // seasonId and does `seasonId.toString()` in the Redis lookup, so a null id
  // NPEs → a blank 500. An empty list is fine — the service falls back to the
  // "no current season". `SeasonNameAndIdVO` element key is `seasonName`.
  const cropSeasons = getSelectedCropSeasonIds().map(seasonId => ({
    seasonId,
    seasonName: ''
  }));
  return {
    ...(id ? { id } : {}),
    invoiceNumber: values.invoiceNumber,
    vendor: values.vendor,
    invoiceDateEpoch,
    invoiceDueDateEpoch,
    cropSeasons,
    flagIds: values.tagIds,
    notes: values.notes,
    invoiceProducts: values.invoiceProducts
      .filter(p => Boolean(p.productId))
      .map(p => ({
        ...(p.id ? { id: p.id } : {}),
        ...(p.trackingId ? { trackingId: p.trackingId } : {}),
        productId: p.productId,
        // The invoice service picks the unit resolver by this flag: `true` →
        // custom-data-service, `false` → master-list. Sending `false` for a
        // genuine org/custom product makes the master-list lookup return a
        // NullUnit → "The Compatable Units List is empty. Measure = 0.0" → 500.
        // Pass the product's real custom flag through (normalizing undefined →
        // false). See FR-095.
        custom: Boolean(p.custom),
        totalQuantityValue: p.totalQuantityValue,
        totalQuantityUnit: p.totalQuantityUnit,
        totalCostValue: p.totalCostValue,
        totalCostCurrency: 'USD',
        // The invoice service builds the compatible-units list from
        // getMeasure(1.0, productDensity); a 0/absent density yields an empty
        // list → "The Compatable Units List is empty. Measure = 0.0, Density =
        // 0.0" → 500. Mirror the shared Applications product formatter's `|| 1`
        // floor so a catalog product whose density is 0 still validates; the
        // Fill Product drawer supplies the real density when the catalog has
        // one. (The backend resolves the package unit itself from productId, so
        // we do NOT send productStandardPackageUnit.)
        productDensity: p.productDensity || 1,
        // Explicitly null, never omitted: an absent field is nulled by JPA's
        // dirty-checking on the Java service but skipped by TypeORM on the
        // Node one, so omitting it would clear the rate on one and leave a
        // stale one on the other.
        unitPriceValue: p.priceValue ?? null,
        // Blank means "no unit chosen"; send it as null so the column holds
        // one spelling of absent rather than two.
        unitPriceUnit: p.priceUnit?.trim() || null
      }))
  };
};

export interface UseInvoiceMutationsReturn {
  saving: boolean;
  error: string;
  setError: (msg: string) => void;
  createInvoice: (values: InvoiceFormValues) => Promise<unknown>;
  updateInvoice: (id: string, values: InvoiceFormValues) => Promise<unknown>;
  /**
   * Promotes the prices the user marked on the invoice to the products' list
   * prices. Resolves `true` when there was nothing to send or the send
   * succeeded, and `false` when it failed — the caller decides what to tell the
   * user, because by then the invoice itself is already saved.
   */
  saveListPrices: (values: InvoiceFormValues) => Promise<boolean>;
  /** Raw error from the most recent create/update attempt (for Amplitude), or `undefined` on success. */
  getLastError: () => unknown;
}

const useInvoiceMutations = (): UseInvoiceMutationsReturn => {
  const [, dispatch] = useContext(Context) as any;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const lastErrorRef = useRef<unknown>(undefined);
  const inventoryProductApi = useInventoryApi(
    'inventory/product'
  ) as ProductListApis['inventoryProductApi'];
  const listPriceApi = useInventoryApi(
    'inventory/products-prices'
  ) as ProductListApis['listPriceApi'];

  // A product used on an invoice must show on the Products page. The invoice
  // service syncs its lines into the inventory itself, but that sync is
  // fire-and-forget on the backend and a first-time product can be left off
  // the list, so after a successful save any line whose product is still
  // missing from the list is registered the way the Products page adds one.
  // Runs in the background and never rejects: the invoice is already saved,
  // so the save's success feedback and timing must not wait on it, and a
  // failure is logged (no error banner) rather than shown as a failed save.
  const persistToProductList = (values: InvoiceFormValues) => {
    registerInvoiceProductsOnList(values.invoiceProducts, {
      inventoryProductApi,
      listPriceApi
    }).catch((err: unknown) => {
      datadogLogs.logger.error(
        'Error adding invoice products to the product list',
        { error: err }
      );
      parseServerError(dispatch, { hideError: true })(err);
    });
  };

  const createInvoice = async (values: InvoiceFormValues) => {
    setSaving(true);
    lastErrorRef.current = undefined;
    const body = mapFormToApi(values);
    const { promise } = (invoiceApi as any).create(body);
    return promise
      .then((result: unknown) => {
        persistToProductList(values);
        return result;
      })
      .catch(catchCancel)
      .catch((err: any) => {
        lastErrorRef.current = err;
        const msg = handleValidationError(err);
        if (msg) {
          setError(`Error creating invoice: ${msg}`);
        } else {
          parseServerError(dispatch)(err);
        }
      })
      .finally(() => setSaving(false));
  };

  const updateInvoice = async (id: string, values: InvoiceFormValues) => {
    setSaving(true);
    lastErrorRef.current = undefined;
    const body = mapFormToApi(values, id);
    const { promise } = invoiceApi.update(id, body);
    return promise
      .then((result: unknown) => {
        persistToProductList(values);
        return result;
      })
      .catch(catchCancel)
      .catch((err: any) => {
        lastErrorRef.current = err;
        const msg = handleValidationError(err);
        if (msg) {
          setError(`Error updating invoice: ${msg}`);
        } else {
          parseServerError(dispatch)(err);
        }
      })
      .finally(() => setSaving(false));
  };

  const saveListPrices = async (values: InvoiceFormValues) => {
    const updates = buildListPriceUpdates(values.invoiceProducts);
    if (!updates.length) return true;

    // Not the hook's `listPriceApi` declared below, which is the same endpoint
    // typed for product-list registration and would shadow this name.
    const { promise } = listPriceResource.create(updates);
    return promise
      .then(() => true)
      .catch((err: any) => {
        // A cancelled request is not a failure the user needs telling about.
        try {
          catchCancel(err);
          return true;
        } catch {
          // fall through to the real failure path below
        }
        // Deliberately not routed through parseServerError: the invoice has
        // already been saved by this point, and a global error banner would
        // read as though it had not been.
        datadogLogs.logger.error('Error saving invoice product list prices', {
          error: err
        });
        return false;
      });
  };

  return {
    saving,
    error,
    setError,
    createInvoice,
    updateInvoice,
    saveListPrices,
    getLastError: () => lastErrorRef.current
  };
};

export default useInvoiceMutations;
